import { useEffect, useRef, useState } from 'react'

/**
 * Drives a YouTube embed through the official IFrame Player API.
 *
 * The alternative — posting `{"event":"command",…}` at the iframe — is an
 * undocumented internal channel: it needs a handshake the player only answers
 * once it has booted, gives no way to know when that is, and reports nothing
 * back. Commands sent a moment too early are dropped silently, which is why a
 * presenter's pause reached every viewer and stopped nothing.
 *
 * The API costs one script from youtube.com, loaded once per page and only
 * when a YouTube video is actually presented. In exchange the player says when
 * it is ready, answers `getCurrentTime()` — so a late joiner can be put where
 * the room really is rather than where arithmetic guesses — and reports the
 * presenter's own play/pause, so using YouTube's controls syncs the room just
 * as our buttons do.
 *
 * Anything that is not a YouTube embed (Vimeo, Drive) leaves this alone and
 * keeps the postMessage path.
 */

type YTPlayer = {
  playVideo: () => void
  pauseVideo: () => void
  seekTo: (seconds: number, allowSeekAhead: boolean) => void
  getCurrentTime: () => number
  getPlayerState: () => number
  destroy: () => void
}

declare global {
  interface Window {
    YT?: {
      Player: new (el: HTMLElement, opts: Record<string, unknown>) => YTPlayer
      PlayerState: { PLAYING: number; PAUSED: number; ENDED: number; BUFFERING: number }
    }
    onYouTubeIframeAPIReady?: () => void
  }
}

/** Is this an embed the API can drive? */
export function isYouTubeEmbed(url: string): boolean {
  return /(?:^|\.)youtube(?:-nocookie)?\.com\/embed\//i.test(url)
}

let apiPromise: Promise<void> | null = null

/** Loads the API once per page. Concurrent callers share the same promise. */
function loadYouTubeApi(): Promise<void> {
  if (window.YT?.Player) return Promise.resolve()
  if (apiPromise) return apiPromise

  apiPromise = new Promise<void>((resolve, reject) => {
    // The API calls this global when it is ready. Chained rather than
    // overwritten: something else on the page may be waiting for it too.
    const previous = window.onYouTubeIframeAPIReady
    window.onYouTubeIframeAPIReady = () => {
      previous?.()
      resolve()
    }
    const script = document.createElement('script')
    script.src = 'https://www.youtube.com/iframe_api'
    script.async = true
    script.onerror = () => {
      // Blocked by a network policy or an extension. The caller stays on the
      // postMessage path rather than losing playback entirely.
      apiPromise = null
      reject(new Error('YouTube API failed to load'))
    }
    document.head.appendChild(script)
  })
  return apiPromise
}

export type YouTubeControl = {
  /** True once the player answers commands. Until then the caller should fall
   *  back to postMessage. */
  ready: boolean
  play: () => void
  pause: () => void
  seekTo: (seconds: number) => void
  /** Current position in seconds, or null when the player cannot be asked. */
  position: () => number | null
  paused: () => boolean | null
}

export function useYouTubePlayer(args: {
  iframeRef: React.RefObject<HTMLIFrameElement | null>
  /** Only true for a YouTube embed that is actually on screen. */
  enabled: boolean
  /** Changes identity when the video changes, so the player is rebuilt. */
  srcKey: string
  /** The local player started or stopped. Used by the presenter to tell the
   *  room, so YouTube's own controls drive everyone. */
  onStateChange?: (playing: boolean, position: number) => void
}): YouTubeControl {
  const { iframeRef, enabled, srcKey, onStateChange } = args
  const playerRef = useRef<YTPlayer | null>(null)
  const [ready, setReady] = useState(false)

  // Behind a ref so a caller's inline closure does not tear down the player.
  const stateRef = useRef(onStateChange)
  stateRef.current = onStateChange

  useEffect(() => {
    if (!enabled) return
    let cancelled = false

    loadYouTubeApi()
      .then(() => {
        const el = iframeRef.current
        if (cancelled || !el || !window.YT?.Player) return
        // Binding to an existing iframe rather than replacing it: the element
        // is already carrying the right src, and letting the API build its own
        // would reload the video.
        playerRef.current = new window.YT.Player(el, {
          events: {
            onReady: () => {
              if (!cancelled) setReady(true)
            },
            onStateChange: (e: { data: number }) => {
              const S = window.YT?.PlayerState
              if (!S) return
              if (e.data !== S.PLAYING && e.data !== S.PAUSED) return
              const at = playerRef.current?.getCurrentTime?.() ?? 0
              stateRef.current?.(e.data === S.PLAYING, at)
            },
          },
        })
      })
      .catch(() => {
        // Left on the postMessage path.
      })

    return () => {
      cancelled = true
      setReady(false)
      // Deliberately NOT destroy(): it removes the iframe from the DOM, and
      // that iframe belongs to React — which then tries to remove the same node
      // itself and throws "the node to be removed is not a child of this one",
      // taking the meeting down with it. Dropping the reference is enough; the
      // caller keys the iframe on its src, so a new video gets a new element
      // and this player is left to be collected with it.
      playerRef.current = null
    }
  }, [enabled, srcKey, iframeRef])

  return {
    ready,
    play: () => playerRef.current?.playVideo(),
    pause: () => playerRef.current?.pauseVideo(),
    seekTo: (seconds: number) => playerRef.current?.seekTo(Math.max(0, seconds), true),
    position: () => {
      try {
        const t = playerRef.current?.getCurrentTime()
        return typeof t === 'number' && Number.isFinite(t) ? t : null
      } catch {
        return null
      }
    },
    paused: () => {
      try {
        const s = playerRef.current?.getPlayerState()
        const S = window.YT?.PlayerState
        if (typeof s !== 'number' || !S) return null
        return s === S.PAUSED || s === S.ENDED
      } catch {
        return null
      }
    },
  }
}
